// localStorage cache for an ad's keyframe metadata. The keyframe data
// itself (shot order, timestamps, signed URLs) is stable per ad, but the
// signed URLs expire on the order of minutes. We still cache the array for
// up to 24h so the Scripts tab can paint thumbnails instantly without
// waiting on /ads/:adId. A background revalidation refreshes the signed
// URLs; the Refresh button on the ad detail busts both the local and
// server caches.

export interface Keyframe {
  shotOrder: number;
  startTime: number;
  endTime: number;
  durationSec: number;
  url: string;
}

const TTL_MS = 24 * 60 * 60 * 1000;
const KEY_PREFIX = 'kf:v1:';

interface Entry {
  ts: number;
  keyframes: Keyframe[];
}

export function getCachedKeyframes(adId: string): Keyframe[] | null {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + adId);
    if (!raw) return null;
    const entry = JSON.parse(raw) as Entry;
    if (!entry || typeof entry.ts !== 'number') return null;
    if (Date.now() - entry.ts > TTL_MS) {
      localStorage.removeItem(KEY_PREFIX + adId);
      return null;
    }
    return Array.isArray(entry.keyframes) ? entry.keyframes : null;
  } catch (_) {
    return null;
  }
}

export function setCachedKeyframes(adId: string, keyframes: Keyframe[]): void {
  try {
    const entry: Entry = { ts: Date.now(), keyframes };
    localStorage.setItem(KEY_PREFIX + adId, JSON.stringify(entry));
  } catch (_) {
    // localStorage full or unavailable — silently skip. The UI will just
    // re-fetch on next render.
  }
}

export function clearCachedKeyframes(adId: string): void {
  try {
    localStorage.removeItem(KEY_PREFIX + adId);
  } catch (_) { /* ignore */ }
}
